<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="true" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Upload Slate Video</h3>
              <p class="text-sm text-gray-400 mt-0.5">Add a new slate to your library</p>
            </div>
            <button @click="close" :disabled="uploading" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 pb-6">
            <div class="mb-5">
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Video File</label>
              <div
                @click="!uploading && $refs.fileInput.click()"
                @dragover.prevent="dragOver = true"
                @dragleave="dragOver = false"
                @drop.prevent="onFileDrop"
                :class="['border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors', dragOver ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300', uploading ? 'opacity-50 cursor-not-allowed' : '']"
              >
                <input ref="fileInput" type="file" class="hidden" accept="video/mp4" @change="onFileChanged" :disabled="uploading" />
                <div v-if="!file">
                  <svg class="w-8 h-8 text-gray-400 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <p class="text-sm text-gray-500 font-medium">Click or drag to upload</p>
                  <p class="text-xs text-gray-400 mt-1">MP4 files only</p>
                </div>
                <div v-else class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <svg class="w-5 h-5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M17 7l-2-4H9L7 7"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0 text-left">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">{{ $filters.formatBytes(file.size) }}</p>
                  </div>
                  <button v-if="!uploading" @click.stop="clearFile" class="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Upload progress -->
            <div v-if="uploading" class="mb-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">Uploading...</span>
                <span class="text-sm font-data text-gray-500">{{ uploadProgress }}%</span>
              </div>
              <div class="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300" :style="{ width: uploadProgress + '%' }" />
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
            <button @click="close" :disabled="uploading" class="btn-secondary">Cancel</button>
            <button @click="uploadFile" :disabled="!file || uploading" class="btn-primary">
              <svg v-if="!uploading" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              {{ uploading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

const CHUNK_SIZE = 8 * 1024 * 1024

export default {
  name: 'UploadSlate',
  emits: ['closed', 'created'],

  data() {
    return {
      file: null,
      dragOver: false,
      uploading: false,
      uploadProgress: 0
    }
  },

  methods: {
    onFileChanged(e) {
      const f = e.target.files?.[0]
      if (!f) return
      if (f.type !== 'video/mp4') { notifyError('Only MP4 files are supported'); return }
      this.file = f
    },

    onFileDrop(e) {
      this.dragOver = false
      const f = e.dataTransfer.files?.[0]
      if (!f) return
      if (f.type !== 'video/mp4') { notifyError('Only MP4 files are supported'); return }
      this.file = f
    },

    clearFile() {
      this.file = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    close() {
      if (this.uploading) return
      this.$emit('closed')
    },

    async uploadFile() {
      if (!this.file || this.uploading) return
      this.uploading = true
      this.uploadProgress = 0

      try {
        const res = await api.post('/slates', {
          filename: this.file.name,
          contentType: this.file.type,
          size: this.file.size
        })

        // Tell the parent to reload the list — the new slate row shows
        // immediately in the "uploading" state, and the list's poller
        // picks it up from there.
        this.$emit('created')

        const uploadUrl = res.data?.url
        if (!uploadUrl) throw new Error('No upload URL received')

        let offset = 0
        while (offset < this.file.size) {
          const chunk = this.file.slice(offset, offset + CHUNK_SIZE)
          const end = Math.min(offset + CHUNK_SIZE, this.file.size)

          await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': this.file.type,
              'Content-Range': `bytes ${offset}-${end - 1}/${this.file.size}`
            },
            body: chunk
          })

          offset = end
          this.uploadProgress = Math.round((offset / this.file.size) * 100)
        }

        notifySuccess('Slate uploaded successfully')
        this.$emit('closed')
      } catch (err) {
        notifyError(err?.message || 'Upload failed')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
